/**
 * Capture QoS — cross-client video pipeline telemetry (schema v1).
 *
 * Lifecycle events:
 *   camera_open → recording_start → system_sample* → segment_rotate →
 *   encode_done → upload_start → upload_done → recording_stop
 *
 * Batches flush to POST /api/mobileAPI/captureQos/batch.
 */

import { ip_back } from "../config";
import { getNetworkTransport, isWifiOnlyVideoUploadEnabled } from "./videoUploadNetwork";

export const CAPTURE_QOS_SCHEMA_VERSION = 1;

export const CAPTURE_QOS_FLOWS = Object.freeze([
  "championship",
  "eval",
  "off_table_chrono",
  "coach_table",
  "instruction",
]);

export const CAPTURE_QOS_EVENTS = Object.freeze([
  "camera_open",
  "recording_start",
  "system_sample",
  "segment_rotate",
  "encode_done",
  "upload_start",
  "upload_done",
  "recording_stop",
]);

const MAX_RING = 200;
const MAX_PENDING = 80;
const FLUSH_MAX_EVENTS = 20;
const FLUSH_INTERVAL_MS = 30_000;
const SAMPLE_INTERVAL_MS = 5_000;

const ring = [];
const pending = [];
const listeners = new Set();

let context = {
  run_id: null,
  session_id: null,
  capture_id: null,
  clip_id: null,
  flow: null,
  club_id: null,
  ts_id: null,
  match_id: null,
};

let sampleTimer = null;
let flushTimer = null;
let flushInflight = false;
let pageHooksInstalled = false;
let clientFingerprint = null;

function nowMs() {
  return Date.now();
}

function toFiniteNumber(value, fallback = null) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function simpleHash(str) {
  const s = String(str || "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

function detectOs() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac OS X/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";
  return "unknown";
}

function detectBrowser() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/CriOS/i.test(ua)) return "chrome_ios";
  if (/FxiOS/i.test(ua)) return "firefox_ios";
  if (/EdgiOS|Edg\//i.test(ua)) return "edge";
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return "chrome";
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return "safari";
  if (/Firefox\//i.test(ua)) return "firefox";
  return "unknown";
}

function jsHeapMb() {
  try {
    const mem = performance?.memory;
    if (mem?.usedJSHeapSize) {
      return Math.round(mem.usedJSHeapSize / (1024 * 1024));
    }
  } catch {
    /* Safari */
  }
  return null;
}

function deviceMemoryGb() {
  try {
    return navigator.deviceMemory != null ? Number(navigator.deviceMemory) : null;
  } catch {
    return null;
  }
}

function connectionStats() {
  try {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection ||
      null;
    if (!conn) {
      return { downlink: null, rtt: null, effectiveType: null };
    }
    return {
      downlink: toFiniteNumber(conn.downlink),
      rtt: toFiniteNumber(conn.rtt),
      effectiveType: conn.effectiveType || null,
    };
  } catch {
    return { downlink: null, rtt: null, effectiveType: null };
  }
}

function batteryCharging() {
  // Async Battery API is not polled here; leave null on web.
  return null;
}

export function buildClientInfo() {
  if (clientFingerprint) return { ...clientFingerprint };
  const ua =
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  clientFingerprint = {
    platform: "web",
    os: detectOs(),
    browser: detectBrowser(),
    app_version:
      (typeof process !== "undefined" &&
        process.env?.REACT_APP_VERSION) ||
      null,
    device_model: null,
    device_memory_gb: deviceMemoryGb(),
    ua_hash: simpleHash(ua),
  };
  return { ...clientFingerprint };
}

/** Read ?captureQosRun= from URL or localStorage. */
export function resolveCaptureQosRunId() {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = String(q.get("captureQosRun") || "").trim();
    if (fromUrl) {
      try {
        localStorage.setItem("SMARTTT_CAPTURE_QOS_RUN", fromUrl);
      } catch {
        /* ignore */
      }
      return fromUrl.slice(0, 128);
    }
    const stored = String(
      localStorage.getItem("SMARTTT_CAPTURE_QOS_RUN") || ""
    ).trim();
    return stored ? stored.slice(0, 128) : null;
  } catch {
    return null;
  }
}

export function setCaptureQosRunId(runId) {
  const id = runId != null ? String(runId).trim().slice(0, 128) : "";
  context.run_id = id || null;
  if (typeof window === "undefined") return;
  try {
    if (id) localStorage.setItem("SMARTTT_CAPTURE_QOS_RUN", id);
    else localStorage.removeItem("SMARTTT_CAPTURE_QOS_RUN");
  } catch {
    /* ignore */
  }
}

export function getCaptureQosContext() {
  return { ...context };
}

/**
 * Merge identity fields used on subsequent events.
 * @param {Partial<typeof context>} patch
 */
export function setCaptureQosContext(patch = {}) {
  if (!patch || typeof patch !== "object") return getCaptureQosContext();
  const next = { ...context };
  for (const key of Object.keys(context)) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      const v = patch[key];
      next[key] =
        v == null || v === ""
          ? null
          : typeof v === "string"
            ? v.slice(0, 128)
            : v;
    }
  }
  if (next.flow && !CAPTURE_QOS_FLOWS.includes(next.flow)) {
    next.flow = String(next.flow).slice(0, 64);
  }
  if (!next.run_id) {
    next.run_id = resolveCaptureQosRunId();
  }
  context = next;
  return getCaptureQosContext();
}

function notify() {
  const snapshot = getCaptureQosEntries();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

function authHeader() {
  try {
    const token =
      (typeof window !== "undefined" && localStorage.getItem("token")) || "";
    return token ? { Authorization: token } : {};
  } catch {
    return {};
  }
}

function trimPending() {
  while (pending.length > MAX_PENDING) {
    const idx = pending.findIndex((e) => e.event === "system_sample");
    if (idx >= 0) pending.splice(idx, 1);
    else pending.shift();
  }
}

/**
 * Build a schema-v1 event envelope.
 * @param {string} event
 * @param {object} [data]
 */
export function buildCaptureQosEvent(event, data = null) {
  const label = String(event || "unknown").slice(0, 64);
  const payload = data && typeof data === "object" ? { ...data } : {};
  const client = buildClientInfo();
  const conn = connectionStats();
  return {
    schema_version: CAPTURE_QOS_SCHEMA_VERSION,
    event: label,
    ts: nowMs(),
    run_id: context.run_id || resolveCaptureQosRunId(),
    session_id: context.session_id,
    capture_id: context.capture_id,
    clip_id: context.clip_id,
    flow: context.flow,
    club_id: context.club_id,
    ts_id: context.ts_id,
    match_id: context.match_id,
    client,
    transport: getNetworkTransport(),
    wifi_only: isWifiOnlyVideoUploadEnabled(),
    network: conn,
    ...payload,
  };
}

/**
 * Append a Capture QoS event to the ring + pending flush queue.
 * @param {string} event
 * @param {object|null} [data]
 */
export function appendCaptureQos(event, data = null) {
  if (!context.run_id) {
    context.run_id = resolveCaptureQosRunId();
  }
  const entry = buildCaptureQosEvent(event, data);
  ring.push(entry);
  while (ring.length > MAX_RING) ring.shift();
  pending.push(entry);
  trimPending();
  notify();
  ensurePageHooks();
  scheduleFlush();
  return entry;
}

export function getCaptureQosEntries() {
  return [...ring];
}

export function clearCaptureQos() {
  ring.length = 0;
  pending.length = 0;
  notify();
}

export function subscribeCaptureQos(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  listener(getCaptureQosEntries());
  return () => listeners.delete(listener);
}

/** Compact device/system snapshot for system_sample events. */
export function collectSystemSample(extra = {}) {
  const conn = connectionStats();
  return {
    js_heap_mb: jsHeapMb(),
    device_memory_gb: deviceMemoryGb(),
    downlink: conn.downlink,
    rtt: conn.rtt,
    effective_type: conn.effectiveType,
    transport: getNetworkTransport(),
    battery_charging: batteryCharging(),
    thermal_state: null,
    cpu_percent: null,
    ...extra,
  };
}

export function startCaptureQosSampling(extraFactory = null) {
  stopCaptureQosSampling();
  const tick = () => {
    const extra =
      typeof extraFactory === "function" ? extraFactory() || {} : {};
    appendCaptureQos("system_sample", collectSystemSample(extra));
  };
  tick();
  sampleTimer = setInterval(tick, SAMPLE_INTERVAL_MS);
  if (typeof sampleTimer?.unref === "function") sampleTimer.unref();
}

export function stopCaptureQosSampling() {
  if (sampleTimer) {
    clearInterval(sampleTimer);
    sampleTimer = null;
  }
}

function scheduleFlush() {
  if (flushTimer || flushInflight) return;
  if (pending.length >= FLUSH_MAX_EVENTS) {
    void flushCaptureQos();
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushCaptureQos();
  }, FLUSH_INTERVAL_MS);
  if (typeof flushTimer?.unref === "function") flushTimer.unref();
}

/**
 * Effective bitrate from encoded bytes and duration seconds.
 */
export function effectiveBitrateBps(bytes, durationSec) {
  const b = Number(bytes);
  const d = Number(durationSec);
  if (!(Number.isFinite(b) && b > 0 && Number.isFinite(d) && d > 0)) return null;
  return Math.round((b * 8) / d);
}

/**
 * Upload throughput in megabits/sec.
 */
export function uploadMbps(bytes, uploadMs) {
  const b = Number(bytes);
  const ms = Number(uploadMs);
  if (!(Number.isFinite(b) && b > 0 && Number.isFinite(ms) && ms > 0)) {
    return null;
  }
  return Math.round(((b * 8) / (ms / 1000) / 1_000_000) * 1000) / 1000;
}

/**
 * Classify upload/encode errors into stable error_class strings.
 */
export function classifyCaptureQosError(err, httpStatus = null) {
  const status = toFiniteNumber(httpStatus);
  if (status != null) {
    if (status >= 500) return "5xx";
    if (status >= 400) return "4xx";
  }
  const msg = String(err?.message || err || "").toLowerCase();
  const code = String(err?.code || "").toUpperCase();
  if (code === "WIFI_REQUIRED" || /wi-?fi|wifi_required/.test(msg)) {
    return "wifi_blocked";
  }
  if (code === "UPLOAD_OFFLINE" || /offline|no network/.test(msg)) {
    return "offline";
  }
  if (/timeout|timed out|aborted/.test(msg)) return "timeout";
  if (/out of memory|oom|allocation/.test(msg)) return "oom";
  if (/thermal/.test(msg)) return "thermal_throttle";
  if (/encode|mediarecorder|webcodecs|codec/.test(msg)) return "encode_fail";
  if (/network|failed to fetch|load failed/.test(msg)) return "network";
  return "error";
}

/**
 * Compact summary to stamp onto view_setup / media metadata.
 */
export function buildCaptureQosSummary(partial = {}) {
  return {
    schema_version: CAPTURE_QOS_SCHEMA_VERSION,
    run_id: context.run_id || resolveCaptureQosRunId(),
    flow: context.flow,
    platform: "web",
    ...partial,
  };
}

/**
 * Flush pending events to the server. Safe to call often; no-ops if empty.
 * @param {{ keepalive?: boolean }} [opts]
 */
export async function flushCaptureQos(opts = {}) {
  if (flushInflight || pending.length === 0) return { sent: 0 };
  if (typeof window === "undefined") return { sent: 0 };

  const batch = pending.splice(0, FLUSH_MAX_EVENTS);
  flushInflight = true;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const body = JSON.stringify({
    schema_version: CAPTURE_QOS_SCHEMA_VERSION,
    events: batch,
  });

  try {
    const url = `${ip_back}api/mobileAPI/captureQos/batch`;
    const headers = {
      ...authHeader(),
      "Content-Type": "application/json",
    };
    if (opts.keepalive && typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: "application/json" });
        // sendBeacon cannot set Authorization; fall through to fetch keepalive.
        void blob;
      } catch {
        /* use fetch */
      }
    }
    const resp = await fetch(url, {
      method: "POST",
      headers,
      body,
      keepalive: Boolean(opts.keepalive),
    });
    if (!resp.ok) {
      // Put events back (samples first drop on overflow).
      pending.unshift(...batch);
      trimPending();
      return { sent: 0, status: resp.status };
    }
    if (pending.length > 0) scheduleFlush();
    return { sent: batch.length, status: resp.status };
  } catch {
    pending.unshift(...batch);
    trimPending();
    return { sent: 0 };
  } finally {
    flushInflight = false;
  }
}

function ensurePageHooks() {
  if (pageHooksInstalled || typeof window === "undefined") return;
  pageHooksInstalled = true;
  const onHide = () => {
    void flushCaptureQos({ keepalive: true });
  };
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onHide();
  });
}

/**
 * Wrap an async upload: emits upload_start / upload_done with timing.
 * @param {object} meta
 * @param {() => Promise<any>} uploadFn
 */
export async function trackCaptureQosUpload(meta, uploadFn) {
  const bytes = toFiniteNumber(meta?.bytes, 0) || 0;
  const started = nowMs();
  appendCaptureQos("upload_start", {
    bytes,
    queue_depth: toFiniteNumber(meta?.queue_depth),
    clip_id: meta?.clip_id || context.clip_id,
    capture_id: meta?.capture_id || context.capture_id,
    segment_index: meta?.segment_index ?? null,
  });
  try {
    const result = await uploadFn();
    const upload_ms = nowMs() - started;
    const http_status = toFiniteNumber(
      meta?.httpStatusFromResult?.(result) ?? result?.http_status ?? 200
    );
    appendCaptureQos("upload_done", {
      ok: true,
      bytes,
      upload_ms,
      mbps: uploadMbps(bytes, upload_ms),
      http_status,
      retries: toFiniteNumber(meta?.retries, 0) || 0,
      clip_id: meta?.clip_id || context.clip_id,
      capture_id: meta?.capture_id || context.capture_id,
      segment_index: meta?.segment_index ?? null,
      error_class: null,
    });
    void flushCaptureQos();
    return result;
  } catch (err) {
    const upload_ms = nowMs() - started;
    const http_status = toFiniteNumber(err?.status || err?.response?.status);
    const error_class = classifyCaptureQosError(err, http_status);
    appendCaptureQos("upload_done", {
      ok: false,
      bytes,
      upload_ms,
      mbps: uploadMbps(bytes, upload_ms),
      http_status,
      retries: toFiniteNumber(meta?.retries, 0) || 0,
      clip_id: meta?.clip_id || context.clip_id,
      capture_id: meta?.capture_id || context.capture_id,
      segment_index: meta?.segment_index ?? null,
      error_class,
      message: String(err?.message || err || "").slice(0, 240),
    });
    void flushCaptureQos();
    throw err;
  }
}

/** Test helper — reset module state. */
export function __resetCaptureQosForTests() {
  stopCaptureQosSampling();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  ring.length = 0;
  pending.length = 0;
  flushInflight = false;
  clientFingerprint = null;
  context = {
    run_id: null,
    session_id: null,
    capture_id: null,
    clip_id: null,
    flow: null,
    club_id: null,
    ts_id: null,
    match_id: null,
  };
  listeners.clear();
}
